import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useRafLoop } from "react-use";
import { Components } from "react-markdown/lib/ast-to-react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";

import { groopTheme } from "../../styles/MaterialGroopTheme";

import { TypingIndicator } from "../../Icons/TypingIndicator";

const useStyles = makeStyles((theme) => ({
  headingFixForSiteCss: {
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      fontWeight: "bold",
      fontSize: "revert",
    },
    "& div:not(.typing-indicator):not(.typing-indicator *), \
  & li:not(.typing-indicator):not(.typing-indicator *), \
  & p:not(.typing-indicator):not(.typing-indicator *), \
  & ol:not(.typing-indicator):not(.typing-indicator *), \
  & ul:not(.typing-indicator):not(.typing-indicator *)": {
      listStyle: "revert",
      margin: "unset",
      padding: "unset",
      whiteSpace: "normal",
    },
    "& li:not(.typing-indicator):not(.typing-indicator *), \
    & ol:not(.typing-indicator):not(.typing-indicator *), \
    & ul:not(.typing-indicator):not(.typing-indicator *)": {
      listStylePosition: "outside",
      paddingLeft: "1em",
    },
  },
  table: {
    border: `1px solid ${groopTheme.palette.primary.main}`,
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  th: {
    backgroundColor: groopTheme.palette.primary.main,
  },
}));

interface FakeStreamingMarkdownProps
  extends React.HTMLAttributes<HTMLDivElement> {
  content: string;
  onComplete?: () => void;
  onTick?: () => void;
  shouldFakeStreaming: boolean;
}

export const FakeStreamingMarkdown = ({
  content,
  onComplete,
  onTick,
  shouldFakeStreaming,
}: FakeStreamingMarkdownProps) => {
  const [splitContent, setSplitContent] = useState<string[]>([]);
  const [ticks, setTicks] = useState(0);
  const [loopStop, loopStart] = useRafLoop(() => setTicks((tick) => tick + 1));

  const [loading, setLoading] = useState(true);

  const finishedStreaming = useCallback(() => {
    setLoading(false);
    onComplete?.();
  }, [onComplete]);

  useEffect(() => {
    if (shouldFakeStreaming) {
      setTicks(0);
      if (content.trim().length === 0) {
        setSplitContent([]);
        loopStop();
        setTicks(0);
        finishedStreaming();
      } else {
        const split = content.split(/(\s+)/);
        setSplitContent(split);
        loopStart();
      }
    } else {
      setSplitContent([]);
      loopStop();
      finishedStreaming();
    }
  }, [
    content,
    finishedStreaming,
    loopStart,
    loopStop,
    onComplete,
    shouldFakeStreaming,
  ]);

  useEffect(() => {
    if (
      shouldFakeStreaming &&
      splitContent.length > 0 &&
      ticks >= splitContent.length
    ) {
      loopStop();
      finishedStreaming();
    } else if (shouldFakeStreaming && ticks > 0) {
      onTick?.();
    }
  }, [
    finishedStreaming,
    loopStop,
    onComplete,
    onTick,
    shouldFakeStreaming,
    splitContent.length,
    ticks,
  ]);

  const classes = useStyles();

  const components = useMemo<Components>(
    () => ({
      table: ({ children }: { children?: ReactNode }) => (
        <Table className={classes.table}>{children}</Table>
      ),
      tbody: ({ children }: { children?: ReactNode }) => (
        <TableBody>{children}</TableBody>
      ),
      td: ({ children }: { children?: ReactNode }) => (
        <TableCell className="text-center">{children}</TableCell>
      ),
      th: ({ children }: { children?: ReactNode }) => (
        <TableCell className={`${classes.th} text-bold text-center`}>
          {children}
        </TableCell>
      ),
      thead: ({ children }: { children?: ReactNode }) => (
        <TableHead>{children}</TableHead>
      ),
      tr: ({ children }: { children?: ReactNode }) => (
        <TableRow>{children}</TableRow>
      ),
    }),
    [classes.table, classes.th],
  );

  return (
    <div>
      {shouldFakeStreaming ? (
        <>
          <div className={classes.headingFixForSiteCss}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
              {splitContent.slice(0, ticks).join("")}
            </ReactMarkdown>
            {(ticks < splitContent.length || loading) && (
              <div className="place-self-start">
                <TypingIndicator />
              </div>
            )}
          </div>
        </>
      ) : (
        <div className={classes.headingFixForSiteCss}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};
